import { type CSSProperties, useEffect, useRef } from 'react';

import { totalMax, totalScore } from '../../domain/scoring';
import { toNumber } from '../../domain/points';
import { usePoints, useT } from '../../i18n';
import {
  selectAssessment,
  selectCandidate,
  selectExam,
  selectStation,
  selectTasks,
  useAppStore,
} from '../../store/useAppStore';
import { kannBeenden } from '../../platform/beenden';
import { zeitAbgelaufen } from '../../platform/haptics';
import { ARM_MS, formatClock, useTimerStore } from '../../store/useTimerStore';
import { Icon } from '../../ui/Icon';
import { useAnimatedNumber } from '../../ui/useAnimatedNumber';
import { Rueckstandspille } from './Rueckstandspille';
import styles from './Header.module.css';

/** Kopfzeile: Station links, Prüfling, Uhr und Gesamtpunktzahl rechts. */
export function Header() {
  const t = useT();
  const points = usePoints();
  const exam = useAppStore(selectExam);
  const station = useAppStore(selectStation);
  const tasks = useAppStore(selectTasks);
  const candidate = useAppStore(selectCandidate);
  const assessment = useAppStore(selectAssessment);
  const examinerId = useAppStore((s) => s.examinerId);
  const timerShow = useAppStore((s) => s.timerShow);
  // ⚠️ Das **Objekt** greifen, nicht drei Felder einzeln und auch kein neues zusammenbauen: der
  // Store gibt bei gleicher Lage dasselbe Objekt zurück, damit die Kopfzeile beim
  // Zehn-Sekunden-Takt der Auswahl nicht jedes Mal neu zeichnet.
  const katalogLage = useAppStore((s) => s.katalogLage);
  const openSheet = useAppStore((s) => s.openSheet);
  const setBeendenOffen = useAppStore((s) => s.setBeendenOffen);
  const summeZeigen = useAppStore((s) => s.bogenAnzeige.summe);

  const examiner = exam.examiners.find((p) => p.id === examinerId) ?? exam.examiners[0];

  const total = toNumber(totalScore(tasks, assessment));
  const max = toNumber(totalMax(tasks));
  const shown = useAnimatedNumber(total);

  // Breitester Text, den der Kasten je zeigen kann: der ganzzahlige Anteil des Höchstwerts
  // (mehr Stellen kann der erreichte Wert nicht haben) mit zwei Nachkommastellen — mehr rundet
  // `points()` weg. Er reserviert die Breite, damit das Hochzählen nichts verschiebt.
  const widestTotal = `${Math.trunc(max)}${t.format.decimal}00 / ${points(max)}`;

  const left = useTimerStore((s) => s.left);
  const running = useTimerStore((s) => s.running);
  const armLeft = useTimerStore((s) => s.armLeft);
  const press = useTimerStore((s) => s.press);
  const setzeDauer = useTimerStore((s) => s.setzeDauer);
  const tick = useTimerStore((s) => s.tick);
  const signal = useTimerStore((s) => s.ablaufSignal);

  const armed = armLeft > 0;
  // Bei 0:00 stellt der Knopf auf Anfang (`press`), also zeigt er das auch.
  const zurueck = armed || left === 0;

  /**
   * Die Uhr übernimmt die Dauer der Station.
   *
   * ⚠️ `setzeDauer` schreibt nur, wenn sich wirklich etwas ändert; der Aufruf steht in einem
   * Effekt und liefe sonst bei jedem Rendern, was die laufende Uhr auf Anfang zurückstellte.
   */
  useEffect(() => {
    setzeDauer(station.durationSeconds);
  }, [station.durationSeconds, setzeDauer]);

  /**
   * Das Signal, wenn die Stationszeit abgelaufen ist — ein Hinweis, mehr nicht: aufgehalten
   * wird der Prüfer nicht, er schliesst die Station selbst ab.
   *
   * ⚠️ **Nicht beim ersten Rendern.** `ablaufSignal` beginnt bei null; ohne die Wache klopfte
   * das iPad jedes Mal, wenn der Bogen neu aufgebaut wird — etwa nach einem Prüflingswechsel.
   */
  const letztesSignal = useRef(signal);
  useEffect(() => {
    if (signal === letztesSignal.current) return;
    letztesSignal.current = signal;
    // Ist die Stationsuhr ausgeblendet, merkt der Prüfer auch den Ablauf nicht — kein Vibrieren
    // (Entscheidung des Nutzers, 2026-10-10). Die Zeit läuft intern weiter.
    if (!timerShow) return;
    zeitAbgelaufen();
  }, [signal, timerShow]);

  // Ein Taktgeber für die ganze App; er schreibt nur, wenn sich etwas Sichtbares ändert.
  // Er läuft auch bei stehender Uhr weiter, solange das Zurücksetzen scharf ist — sonst
  // liefe dessen Drei-Sekunden-Fenster nie ab.
  useEffect(() => {
    if (!running && !armed) return;
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [running, armed, tick]);

  return (
    <div className={styles.header}>
      <div className={`column ${styles.row}`}>
        <button
          type="button"
          className={`tap ${styles.iconButton}`}
          onClick={() => openSheet({ kind: 'setup' })}
          aria-label={t.header.chooseExam}
        >
          <Icon name="exam" size={21} />
        </button>

        {/*
          Ein Zeichen, eine Aussage — und es führt dorthin, wo das Genaue steht: in die
          Prüfungsauswahl. Hier steht **dass** etwas ist, dort **bei welcher Prüfung**.

          ⚠️ **Kein Zeichen mehr für „Server nicht erreichbar"** (Vorgabe des Nutzers,
          2026-09-22). Bis dahin stand daneben ein roter Stecker, sobald ein Abruf des Katalogs
          scheiterte. Das ist im Prüfungsbetrieb der Regelfall, nicht die Ausnahme: das Gerät
          arbeitet offline weiter, jede Bewertung und jede Datei wird gepuffert und später von
          selbst nachgeschoben. Ein Alarm dafür macht den Prüfer ratlos an einer Stelle, an der
          nichts zu tun ist — und stumpft ihn gegen die Zeichen ab, die wirklich etwas heissen.
          Wie viel noch aussteht, sagt die Rückstandspille nebenan; warum, steht im
          Einstellungsblatt.

          ⚠️ Das verbliebene Zeichen beschreibt den Stand des **letzten** Blicks. Gefragt wird
          nur, solange die Auswahl offen ist; es kann also älter sein als eine Minute. Es zu
          verschweigen, wäre schlechter — der letzte bekannte Befund ist mehr wert als keiner.
        */}
        {katalogLage.veraltet + katalogLage.verwaist > 0 && (
          <button
            type="button"
            className={`tap ${styles.iconButton} ${styles.alarm}`}
            onClick={() => openSheet({ kind: 'setup' })}
            aria-label={t.header.problemExam}
            title={t.header.problemExam}
          >
            <Icon name="warning" size={19} />
          </button>
        )}

        <div className={styles.station}>
          <div className={styles.stationMeta}>
            <span className={styles.kicker}>
              {t.header.stationPrefix} · {station.meta}
            </span>
            <span className={styles.dot} />
            <span className={styles.examiner}>{examiner.name}</span>
          </div>
          <div className={styles.stationName}>{station.label}</div>
        </div>

        <div className={styles.right}>
          <button
            type="button"
            className={`tap ${styles.candidateButton}`}
            onClick={() => openSheet({ kind: 'candidates' })}
          >
            <span className={styles.label}>{t.header.candidate}</span>
            <span className={styles.candidateName}>
              {/* Zwischen Stationswahl und Ausweisscan steht noch niemand vor dem Prüfer.
                  Der Knopf bleibt an derselben Stelle und führt zu derselben Liste — er sagt
                  nur, dass die Wahl noch aussteht. */}
              <span data-empty={!candidate}>{candidate ? candidate.name : t.header.noCandidate}</span>
              <Icon name="chevronDown" size={13} color="var(--dim)" />
            </span>
          </button>

          {timerShow && (
            <>
              <div className={styles.divider} />
              <button
                type="button"
                className={`tap ${styles.clockBox}`}
                onClick={() => openSheet({ kind: 'timer' })}
                aria-label={t.header.setClock}
              >
                <span className={styles.label}>{t.header.remaining}</span>
                <span className={styles.clock} data-warn={left <= 30}>
                  {formatClock(left)}
                </span>
              </button>
              <button
                type="button"
                className={`tap ${styles.timerButton}`}
                data-running={running}
                data-armed={armed}
                onClick={press}
                aria-label={
                  running ? t.header.pauseClock : zurueck ? t.header.resetClock : t.header.startClock
                }
                style={
                  armed
                    ? ({
                        // Der Ring läuft sichtbar ab — er zeigt, wie lange der Knopf noch
                        // „zurücksetzen" bedeutet.
                        '--arm-angle': Math.round((armLeft / ARM_MS) * 360) + 'deg',
                      } as CSSProperties)
                    : undefined
                }
              >
                <span className={styles.timerButtonFace}>
                  <Icon
                    name={running ? 'pause' : zurueck ? 'refresh' : 'play'}
                    size={17}
                  />
                </span>
                {armed && (
                  <span className={styles.timerBadge}>{Math.ceil(armLeft / 1000)}</span>
                )}
              </button>
            </>
          )}

          {/* `show_sum_points` aus: keine Summe im Kopf (seit 2026-10-02). */}
          {summeZeigen && (
            <div className={styles.total}>
              <div className={styles.totalLabel}>{t.header.total}</div>
              <div className={styles.totalValue}>
                <span className={styles.totalMeasure} aria-hidden="true">
                  {widestTotal}
                </span>
                <span>
                  {points(shown)} / {points(max)}
                </span>
              </div>
            </div>
          )}

          {/*
            Die Rückstandsanzeige steht rechts (Vorgabe des Nutzers, 2026-09-26) — seit sie
            aus zwei Zeichen mit Kreis besteht statt aus einer Pille, ist sie schmal genug
            dafür, und rechts sieht der Prüfer ohnehin hin: dort stehen Prüfling, Uhr und
            Punktzahl.

            ⚠️ **Hinter der Punktzahl, aber vor dem Zahnrad.** Das Zahnrad ist ein fester
            Knopf in der Ecke; rückte es nach innen, wanderte ein Ziel, das der Prüfer blind
            trifft. Dasselbe Argument hat schon die Uhr an ihrem Platz gelassen.

            ⚠️ Sie stand bis zum 2026-09-26 **vor** der Station, bei den beiden Warnzeichen —
            mit der Begründung, alles über den Stand des Geräts gehöre links beieinander. Die
            Begründung war nicht falsch, aber die Pille war dort breit und drängte den
            Stationsnamen zusammen; auf dem iPad Air 2 war er dadurch abgeschnitten.
          */}
          <Rueckstandspille />

          <button
            type="button"
            className={`tap ${styles.iconButton}`}
            onClick={() => openSheet({ kind: 'settings' })}
            aria-label={t.header.settings}
          >
            <Icon name="settings" size={19} />
          </button>

          {/*
            Der Weg nach draussen — **nur am Rechner**, und nur, weil es ihn dort sonst nicht
            gäbe: ein Fenster im Vollbild zeigt weder Menüleiste noch Schliessknopf (Auftrag
            des Nutzers, 2026-09-23). Am iPad beendet das Betriebssystem, im Browser schliesst
            man den Tab; dort zeichnet `kannBeenden()` gar nichts.

            ⚠️ Er beendet nicht, er **fragt** — das Schloss steht in `features/beenden/`.
          */}
          {kannBeenden() && (
            <button
              type="button"
              className={`tap ${styles.iconButton}`}
              onClick={() => setBeendenOffen(true)}
              aria-label={t.exit.button}
              title={t.exit.button}
            >
              <Icon name="logout" size={19} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
